import { computed, ref, shallowRef } from 'vue';
import { storeToRefs } from 'pinia';
import type { Step } from '../interfaces';
import { useSessionStore } from '@Stores';
import { ROLES } from '@AuthModule';
import { INTAKE_TYPES } from '@IntakeModule';
import useShoppingCartStore from '../store';
import { CART_STEPS, OUTCOMES_TYPES } from '../enums';
import { isCheckInIntakeType } from '../helpers';
import {
  MShoppingCartStepAddProductsAndServices,
  MShoppingCartStepCompleteFlow,
  MShoppingCartStepDonations,
  MShoppingCartStepInfo,
  MShoppingCartStepMobileCheckout,
  MShoppingCartStepPaymentMethod,
  MShoppingCartStepPersonInfo,
} from '../components/molecules/MShoppingCartStep';
import {
  OShoppingCartStepEquipmentRentalCheckout,
  OShoppingCartStepFosterCheckout,
  OShoppingCartStepFosterEquipmentRental,
  OShoppingCartStepSummary,
} from '../components/organisms';

const ADD_PEOPLE_STEP_HEADERS = {
  adopter: {
    tooltip: 'cart.tooltip.adopterInfo',
    header: {
      icon: 'faAddressCard',
      title: 'cart.stepper.adopter.title',
      subtitle: 'cart.stepper.adopter.subtitle',
    },
  },
  citation: {
    tooltip: 'cart.tooltip.adopterInfo',
    header: {
      icon: 'faAddressCard',
      title: 'cart.stepper.citation.title',
      subtitle: 'cart.stepper.citation.subtitle',
    },
  },
  guardian: {
    tooltip: 'cart.tooltip.summary',
    header: {
      icon: 'faUser',
      title: 'cart.stepper.guardian.title',
      subtitle: 'cart.stepper.guardian.subtitle',
    },
  },
  payor: {
    tooltip: 'cart.tooltip.summary',
    header: {
      icon: 'faUser',
      title: 'cart.stepper.payor.title',
      subtitle: 'cart.stepper.payor.subtitle',
    },
  },
  product: {
    tooltip: 'cart.tooltip.summary',
    header: {
      icon: 'faUser',
      title: 'cart.stepper.buyer.title',
      subtitle: 'cart.stepper.buyer.subtitle',
    },
  },
  donation: {
    tooltip: 'cart.tooltip.summary',
    header: {
      icon: 'faUser',
      title: 'cart.stepper.donor.title',
      subtitle: 'cart.stepper.donor.subtitle',
    },
  },
  contact: {
    tooltip: 'cart.tooltip.summary',
    header: {
      icon: 'faUser',
      title: 'cart.stepper.contact.title',
      subtitle: 'cart.stepper.contact.subtitle',
    },
  },
  renter: {
    tooltip: 'cart.tooltip.summary',
    header: {
      icon: 'faAddressCard',
      title: 'cart.stepper.renter.title',
      subtitle: 'cart.stepper.renter.subtitle',
    },
  },
};

const currentFlow = ref<CART_STEPS[]>([]);
const currentStep = ref<CART_STEPS>(CART_STEPS.OUTCOME_INFO);

const STEPS = ref<Step[]>([
  {
    active: false,
    component: shallowRef(MShoppingCartStepInfo),
    name: CART_STEPS.OUTCOME_INFO,
    tooltip: 'cart.tooltip.outcomeInfo',
    header: {
      icon: 'faListCheck',
      title: 'cart.stepper.outcomeInfo.title',
      subtitle: 'cart.stepper.outcomeInfo.subtitle',
    },
  },
  {
    active: false,
    component: shallowRef(MShoppingCartStepAddProductsAndServices),
    name: CART_STEPS.PRODUCTS_AND_SERVICES,
    tooltip: 'cart.tooltip.addProducts',
    header: {
      icon: 'faBox',
      title: 'cart.stepper.products.title',
      subtitle: 'cart.stepper.products.subtitle',
    },
  },
  {
    active: false,
    component: shallowRef(OShoppingCartStepSummary),
    name: CART_STEPS.SUMMARY,
    tooltip: 'cart.tooltip.summary',
    header: {
      icon: 'faBagShopping',
      title: 'cart.stepper.summary.title',
      subtitle: 'cart.stepper.summary.subtitle',
    },
  },
  {
    active: false,
    component: shallowRef(MShoppingCartStepPersonInfo),
    name: CART_STEPS.ADD_PEOPLE,
    tooltip: 'cart.tooltip.adopterInfo',
    header: {
      icon: 'faAddressCard',
      title: 'cart.stepper.adopter.title',
      subtitle: 'cart.stepper.adopter.subtitle',
    },
  },
  {
    active: false,
    component: shallowRef(MShoppingCartStepMobileCheckout),
    name: CART_STEPS.MOBILE_CHECKOUT,
    tooltip: 'cart.tooltip.mobileCheckout',
    header: {
      icon: 'faMobile',
      title: 'cart.stepper.pendingMobileCheckout.title',
      subtitle: 'cart.stepper.pendingMobileCheckout.subtitle',
    },
  },
  {
    active: false,
    component: shallowRef(MShoppingCartStepPaymentMethod),
    name: CART_STEPS.PAYMENT_METHOD,
    tooltip: 'cart.tooltip.paymentMethod',
    header: {
      icon: 'faWallet',
      title: 'cart.stepper.paymentMethod.title',
      subtitle: 'cart.stepper.paymentMethod.subtitle',
    },
  },
  {
    active: false,
    component: shallowRef(MShoppingCartStepDonations),
    name: CART_STEPS.DONATION,
    tooltip: 'cart.tooltip.donation',
    header: {
      icon: 'faHandHoldingDollar',
      title: 'cart.stepper.donation.title',
      subtitle: 'cart.stepper.donation.subtitle',
    },
  },
  {
    active: false,
    component: shallowRef(OShoppingCartStepEquipmentRentalCheckout),
    name: CART_STEPS.EQUIPMENT_RENTAL_CHECKOUT,
    tooltip: 'cart.tooltip.equipmentRentalCheckout',
    header: {
      icon: 'faToolbox',
      title: 'cart.stepper.equipmentRentalDetails.title',
      subtitle: 'cart.stepper.equipmentRentalDetails.subtitle',
    },
  },
  {
    active: false,
    component: shallowRef(OShoppingCartStepFosterCheckout),
    name: CART_STEPS.FOSTER_CHECKOUT,
    tooltip: 'cart.tooltip.fosterCheckout',
    header: {
      icon: 'faClipboardList',
      title: 'cart.stepper.fosterDetails.title',
      subtitle: 'cart.stepper.fosterDetails.subtitle',
    },
  },
  {
    active: false,
    component: shallowRef(OShoppingCartStepFosterEquipmentRental),
    name: CART_STEPS.FOSTER_EQUIPMENT_RENTAL,
    tooltip: 'cart.tooltip.fosterEquipmentRental',
    header: {
      icon: 'faToolbox',
      title: 'cart.stepper.fosterEquipmentRental.title',
      subtitle: 'cart.stepper.fosterEquipmentRental.subtitle',
    },
  },
  {
    active: false,
    component: shallowRef(MShoppingCartStepCompleteFlow),
    name: CART_STEPS.COMPLETE_CHECK_IN,
    tooltip: 'cart.tooltip.completeCheckIn',
    header: {
      icon: 'faCircleCheck',
      title: 'cart.stepper.completeFlow.titleCheckIn',
      subtitle: 'cart.stepper.completeFlow.subtitleCheckIn',
    },
  },
]);

const previousStep = computed(() => {
  const previousStepIndex = currentFlow.value.indexOf(currentStep.value) - 1;
  return currentFlow.value[previousStepIndex];
});

const nextStep = computed(() => {
  const nextStepIndex = currentFlow.value.indexOf(currentStep.value) + 1;
  const nextStepName = currentFlow.value[nextStepIndex];

  return {
    step: STEPS.value.find(({ name }) => name === nextStepName),
  };
});

const nextStepButton = computed(() => ({
  label: !nextStep.value.step ? 'common.complete' : 'common.nextStep',
  icon: nextStep.value.step ? nextStep.value.step.header.icon : '',
}));

export default function () {
  const cartStore = useShoppingCartStore();
  const { shelterHasProducts } = storeToRefs(useSessionStore());

  // CART_STEPS and OUTCOMES_TYPES enums
  const {
    ADD_PEOPLE,
    COMPLETE_CHECK_IN,
    DONATION,
    EQUIPMENT_RENTAL_CHECKOUT,
    FOSTER_CHECKOUT,
    FOSTER_EQUIPMENT_RENTAL,
    MOBILE_CHECKOUT,
    OUTCOME_INFO,
    PAYMENT_METHOD,
    PRODUCTS_AND_SERVICES,
    SUMMARY,
  } = CART_STEPS;

  const {
    // IN CARE
    ADOPTED,
    CLINIC_OUT,
    DIED_IN_CARE,
    DOA,
    LOST,
    OWNER_REQUESTED_EUTHANASIA,
    RETURN_TO_FIELD,
    RETURN_TO_OWNER,
    SERVICE_OUT,
    SHELTER_EUTHANASIA,
    TRANSFER_OUT,
    WILDLIFE_OUT,

    // LOST & FOUND
    FINDER_KEPT,
    REMAINED_WITH_FINDER,
    REHOMED,
    REUNITED_WITH_OWNER,
    SHELTER_INTAKE,
    LOST_FOUND_RETURNED_TO_FIELD,
    LOST_FOUND_DECEASED,
    UNKNOWN,

    // REHOME
    REHOME,
    REHOME_SHELTER_INTAKE,
    REHOME_UNKNOWN,
    REHOME_DECEASED,
    REMAINED_WITH_OWNER,

    // CITATION
    CITATION,

    // INTAKE
    INTAKE,

    // PRODUCTS
    PRODUCTS,

    // FOSTER
    FOSTER,

    EQUIPMENT_RENTAL,

    // DONATIONS
    DONATIONS,
  } = OUTCOMES_TYPES;

  // NOTE :: CART TYPES
  const PLAIN_OUTCOME_TYPES = [
    DIED_IN_CARE,
    DOA,
    LOST,
    OWNER_REQUESTED_EUTHANASIA,
    REHOME,
    REHOME_SHELTER_INTAKE,
    REHOME_UNKNOWN,
    REHOME_DECEASED,
    REMAINED_WITH_OWNER,
    RETURN_TO_FIELD,
    SHELTER_EUTHANASIA,
    SHELTER_INTAKE,
    LOST_FOUND_RETURNED_TO_FIELD,
    LOST_FOUND_DECEASED,
    TRANSFER_OUT,
    UNKNOWN,
    WILDLIFE_OUT,
  ];

  const OUTCOME_GUARDIAN_TYPES = [
    REUNITED_WITH_OWNER,
    REHOMED,

    // NOTE :: FINDER_KEPT and REMAINED_WITH_FINDER are the same. REMAINED_WITH_FINDER is the one we are using nowadays.
    FINDER_KEPT,
    REMAINED_WITH_FINDER,
  ];

  const OUTCOMES_CART_TYPES = [ADOPTED, CLINIC_OUT, RETURN_TO_OWNER, SERVICE_OUT];

  // NOTE :: CART STEPS

  // PLAIN OUTCOME
  const PLAIN_OUTCOME_STEPS = [OUTCOME_INFO];

  // OUTCOME WITH GUARDIAN
  const OUTCOME_GUARDIAN_STEPS = [OUTCOME_INFO, ADD_PEOPLE];

  // CITATION
  const CITATION_CART_STEPS = [ADD_PEOPLE, DONATION, PAYMENT_METHOD];

  // DONATION
  const DONATION_CART_MANUAL_STEPS = [ADD_PEOPLE, DONATION, PAYMENT_METHOD];

  // INTAKE
  const INTAKE_CART_MANUAL_STEPS = [ADD_PEOPLE, SUMMARY, DONATION, PAYMENT_METHOD];
  const INTAKE_CART_MANUAL_STEPS_SIGNATURE_ONLY = [ADD_PEOPLE, SUMMARY, DONATION, COMPLETE_CHECK_IN];
  const INTAKE_CART_MOBILE_STEPS = [ADD_PEOPLE, SUMMARY, MOBILE_CHECKOUT];
  const INTAKE_CART_SELECTION_STEPS = [ADD_PEOPLE, SUMMARY];

  // PRODUCTS
  const PRODUCTS_CART_MANUAL_STEPS = [PRODUCTS_AND_SERVICES, SUMMARY, ADD_PEOPLE, DONATION, PAYMENT_METHOD];
  const PRODUCTS_CART_MOBILE_STEPS = [PRODUCTS_AND_SERVICES, SUMMARY, MOBILE_CHECKOUT];
  const PRODUCTS_CART_SELECTION_STEPS = [PRODUCTS_AND_SERVICES, SUMMARY];

  function getFosterCartSteps(): CART_STEPS[] {
    const sessionStore = useSessionStore();
    const fosterEquipmentRentalEnabled = !!sessionStore.shelterProfile?.equipment_rental_enabled;
    const fosterEquipmentAvailable = !!cartStore.fosterEquipmentAvailable;
    // Staff without Equipment Rental permission can still run the foster checkout — just skip the
    // rental sub-step so they don't see equipment they can't check out.
    const canCheckOutEquipment = sessionStore.can(ROLES.EQUIPMENT_RENTAL);
    return fosterEquipmentRentalEnabled && fosterEquipmentAvailable && canCheckOutEquipment
      ? [FOSTER_CHECKOUT, FOSTER_EQUIPMENT_RENTAL]
      : [FOSTER_CHECKOUT];
  }

  const EQUIPMENT_RENTAL_CART_STEPS = [ADD_PEOPLE, EQUIPMENT_RENTAL_CHECKOUT];

  // OUTCOMES

  // OUTCOMES CART - NO PRODUCTS (selection = before user picks mobile vs manual)
  const OUTCOMES_CART_MOBILE_STEPS = [OUTCOME_INFO, SUMMARY, MOBILE_CHECKOUT];
  const OUTCOMES_CART_MANUAL_STEPS = [OUTCOME_INFO, SUMMARY, ADD_PEOPLE, DONATION, PAYMENT_METHOD];
  const OUTCOMES_CART_SELECTION_STEPS = [OUTCOME_INFO, SUMMARY];

  // OUTCOMES CART - WITH PRODUCTS
  const OUTCOMES_CART_MOBILE_WITH_PRODUCTS_STEPS = [OUTCOME_INFO, PRODUCTS_AND_SERVICES, SUMMARY, MOBILE_CHECKOUT];
  const OUTCOMES_CART_MANUAL_WITH_PRODUCTS_STEPS = [
    OUTCOME_INFO,
    PRODUCTS_AND_SERVICES,
    SUMMARY,
    ADD_PEOPLE,
    DONATION,
    PAYMENT_METHOD,
  ];
  const OUTCOMES_CART_SELECTION_WITH_PRODUCTS_STEPS = [OUTCOME_INFO, PRODUCTS_AND_SERVICES, SUMMARY];

  const TRANSFER_OUT_MOBILE_STEPS = [OUTCOME_INFO, MOBILE_CHECKOUT];

  function resetSteps() {
    STEPS.value.forEach((step) => {
      step.active = false;

      // NOTE ::Reset ADD_PEOPLE step to default header (adopter)
      if (step.name === ADD_PEOPLE) {
        const { header, tooltip } = ADD_PEOPLE_STEP_HEADERS.adopter;
        step.header = header;
        step.tooltip = tooltip;
      }
    });

    currentFlow.value = [];
    currentStep.value = CART_STEPS.OUTCOME_INFO;
  }

  function setActiveOutcomeSteps(cartType?: OUTCOMES_TYPES, outcomeType?: OUTCOMES_TYPES | INTAKE_TYPES) {
    resetSteps();

    const type =
      cartType && [INTAKE, CITATION, PRODUCTS, DONATIONS, FOSTER, EQUIPMENT_RENTAL].includes(cartType)
        ? cartType
        : outcomeType ?? cartType ?? null;

    if (!type) return;

    const isDonations = type === DONATIONS;
    const isEquipmentRental = type === EQUIPMENT_RENTAL;
    const isFoster = type === FOSTER;
    const isIntake = type === INTAKE;
    const isCitation = type === CITATION;
    const isPlainOutcome = PLAIN_OUTCOME_TYPES.includes(type as OUTCOMES_TYPES);
    const isGuardianOutcome = OUTCOME_GUARDIAN_TYPES.includes(type as OUTCOMES_TYPES);
    const isCartOutcome = OUTCOMES_CART_TYPES.includes(type as OUTCOMES_TYPES);
    const isProduct = type === 'product';

    const hasProductStep = shelterHasProducts.value && !isIntake && !isCitation;

    // prettier-ignore
    switch (true) {
      case isDonations: return setOutcomeFlow(DONATION_CART_MANUAL_STEPS, 'donation');
      case isEquipmentRental: return setOutcomeFlow(EQUIPMENT_RENTAL_CART_STEPS, 'renter');
      case isFoster: return setOutcomeFlow(getFosterCartSteps());
      case isProduct: return setOutcomeFlow(PRODUCTS_CART_SELECTION_STEPS, 'product');
      case isIntake: {
        const isTransferIn = INTAKE_TYPES.TRANSFER_IN === outcomeType;
        const intakeSelectionFlow = isTransferIn ? [SUMMARY] : INTAKE_CART_SELECTION_STEPS;
        const peopleStepType = isTransferIn ? undefined : 'contact';

        return setOutcomeFlow(intakeSelectionFlow, peopleStepType);
      }
      case isCitation: return setOutcomeFlow(CITATION_CART_STEPS, 'citation');
      case isPlainOutcome: return setOutcomeFlow(PLAIN_OUTCOME_STEPS);
      case isGuardianOutcome: return setOutcomeFlow(OUTCOME_GUARDIAN_STEPS, 'guardian');
      case isCartOutcome: {
        // prettier-ignore
        const outcomePeopleLabel = !outcomeType ? 'adopter'
          : [CLINIC_OUT, OWNER_REQUESTED_EUTHANASIA, SERVICE_OUT].includes(outcomeType as OUTCOMES_TYPES) ? 'guardian'
          : 'adopter';

        // NOTE :: Use selection flow until user picks mobile vs manual checkout
        const outcomeStepsFlow = hasProductStep
          ? OUTCOMES_CART_SELECTION_WITH_PRODUCTS_STEPS
          : OUTCOMES_CART_SELECTION_STEPS;

        setOutcomeFlow(outcomeStepsFlow, outcomePeopleLabel);
        return;
      }
      default: return setOutcomeFlow(PLAIN_OUTCOME_STEPS);
    }
  }

  function setOutcomeFlow(flow: CART_STEPS[], peopleStepType?: keyof typeof ADD_PEOPLE_STEP_HEADERS) {
    const activeSteps: Step[] = [];
    const inactiveSteps: Step[] = [];

    const { isIntake, outcomeType } = storeToRefs(cartStore);
    const isCheckInType = isCheckInIntakeType(outcomeType.value);

    STEPS.value.forEach((step: Step) => {
      const peopleStepNeedsHeaderUpdate = step.name === ADD_PEOPLE && peopleStepType;
      if (peopleStepNeedsHeaderUpdate) {
        const { header, tooltip } = ADD_PEOPLE_STEP_HEADERS[peopleStepType];
        step.header = header;
        step.tooltip = tooltip;
      }

      const mobileCheckoutNeedsHeaderUpdate = step.name === MOBILE_CHECKOUT && isIntake.value;
      if (mobileCheckoutNeedsHeaderUpdate) {
        step.header = {
          ...step.header,
          // prettier-ignore
          title: !isIntake.value ? 'cart.stepper.pendingMobileCheckout.title'
            : isCheckInType ? 'cart.stepper.pendingMobileCheckout.titleCheckin'
            : 'cart.stepper.pendingMobileCheckout.titleIntake',

          // prettier-ignore
          subtitle: !isIntake.value ? 'cart.stepper.pendingMobileCheckout.subtitle'
            : isCheckInType ? 'cart.stepper.pendingMobileCheckout.subtitleCheckin'
            : 'cart.stepper.pendingMobileCheckout.subtitleIntake',
        };
      }
      const completeFlowNeedsHeaderUpdate = step.name === COMPLETE_CHECK_IN && isIntake.value;
      if (completeFlowNeedsHeaderUpdate) {
        step.header = {
          ...step.header,
          title: isCheckInType ? 'cart.stepper.completeFlow.titleCheckIn' : 'cart.stepper.completeFlow.titleIntake',
          subtitle: isCheckInType
            ? 'cart.stepper.completeFlow.subtitleCheckIn'
            : 'cart.stepper.completeFlow.subtitleIntake',
        };
      }
      const isActive = flow.includes(step.name);
      step.active = isActive;

      if (isActive) {
        const position = flow.indexOf(step.name);
        activeSteps[position] = step;
        return;
      }

      inactiveSteps.push(step);
    });

    STEPS.value = [...activeSteps, ...inactiveSteps];

    currentFlow.value = flow;
  }

  function setInitialStep() {
    const { activeCartStep, people, isIntake, isProduct, isService, cartData } = storeToRefs(cartStore);
    const hasPeople = people.value?.people_id;
    const intakeWithPeople = isIntake.value && hasPeople;
    const equipmentWithPeople = cartData.value?.cart_type === EQUIPMENT_RENTAL && hasPeople;

    activeCartStep.value = isProduct.value || isService.value || intakeWithPeople || equipmentWithPeople ? 1 : 0;
  }

  return {
    currentFlow,
    currentStep,
    nextStep,
    nextStepButton,
    previousStep,

    ADD_PEOPLE_STEP_HEADERS,
    OUTCOMES_CART_MANUAL_STEPS,
    OUTCOMES_CART_MANUAL_WITH_PRODUCTS_STEPS,
    OUTCOMES_CART_MOBILE_STEPS,
    OUTCOMES_CART_MOBILE_WITH_PRODUCTS_STEPS,
    OUTCOME_GUARDIAN_TYPES,
    PRODUCTS_CART_MANUAL_STEPS,
    PRODUCTS_CART_MOBILE_STEPS,
    INTAKE_CART_MANUAL_STEPS,
    INTAKE_CART_MANUAL_STEPS_SIGNATURE_ONLY,
    INTAKE_CART_MOBILE_STEPS,
    STEPS,
    TRANSFER_OUT_MOBILE_STEPS,

    setActiveOutcomeSteps,
    setInitialStep,
    setOutcomeFlow,
  };
}
